import { useCallback, useState } from "react";
import { Button, Card, Container, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { useLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import { tickerHookFormValidation } from "../../shared/crud/form-validation";
import DynamicIconList from "../../shared/widgets/DynamicIconList/DynamicIcon";
import { IconPack } from "../../shared/widgets/DynamicIconList/IconPack.enum";
import { TranslationComponent, TranslationForm } from "../../shared/widgets/TranslationComponent";
import { ErrorField } from "../../shared/widgets/TranslationComponent/styles";
import { LoyaltyTag, LoyaltyTagType } from "../domain/LoyaltyTag";
import { ModalActionType, useModal } from "../infrastructure/hooks/modal.hook";
import { useTag, useTags } from "../infrastructure/hooks/tags.hook";
import { loyaltyTagEditMessages } from "./LoyaltyTagEdit.messages";

interface PreferenceDto extends TranslationForm {
  ticker: string;
  icon?: string;
}

export function LoyaltyTagEdit() {
  const { id } = useParams<{ id?: string }>();
  const allowedLocales = usePropertyLocales();
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const property = useProperty();
  const intl = useIntl();
  const { showModal } = useModal();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const { tag, isLoading: isTagLoading } = useTag(brandId, id);
  const { saveTag, updateTag } = useTags(brandId);

  const form = useForm<PreferenceDto>({
    values: tag
      ? {
          ticker: tag.ticker,
          icon: tag.icon,
          translationsList: allowedLocales.map(locale => {
            const translation = tag.translations.find(t => t.locale === locale);
            return {
              locale,
              name: translation?.name ?? "",
              description: translation?.description ?? "",
            };
          }),
        }
      : undefined,
  });

  const handleGoBack = useCallback(() => {
    history.push(`/${property.ticker}/loyalty-preferences/preferences`);
  }, [history, property.ticker]);

  const handleSubmit = useCallback(
    async (data: PreferenceDto) => {
      try {
        setLoading(true);
        if (id && tag) {
          const updatedTag = LoyaltyTag.of({
            ...tag,
            ticker: data.ticker,
            type: LoyaltyTagType.PREFERENCE,
            icon: data.icon,
            translations: data.translationsList,
          });

          await updateTag(updatedTag);
          showModal({ type: ModalActionType.SUCCESS_UPDATE }, handleGoBack);
        } else {
          const loyaltyTag = LoyaltyTag.create({
            ticker: data.ticker,
            type: LoyaltyTagType.PREFERENCE,
            icon: data.icon,
            translations: data.translationsList,
          });

          await saveTag(loyaltyTag);
          showModal({ type: ModalActionType.SUCCESS_CREATE }, handleGoBack);
        }
      } catch (e) {
        setLoading(false);
        if (id) {
          console.error("Error updating loyalty preference", e);
          showModal({ type: ModalActionType.ERROR_UPDATE }, () => {});
        } else {
          console.error("Error creating loyalty preference", e);
          showModal({ type: ModalActionType.ERROR_CREATE }, () => {});
        }
      }
    },
    [handleGoBack, id, saveTag, showModal, tag, updateTag]
  );

  if (!brandId || loading || isTagLoading) {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...loyaltyTagEditMessages.title} />
      </SectionHeading>
      <Container className="p-4" fluid={true}>
        <Card className="p-3">
          <FormProvider {...form}>
            <Form>
              <FormGroup>
                <FormLabel htmlFor="preferenceTicker">
                  <FormattedMessage {...loyaltyTagEditMessages.ticker} /> *
                </FormLabel>
                <FormControl
                  id="preferenceTicker"
                  type="text"
                  {...form.register("ticker", tickerHookFormValidation(intl))}
                />

                {form.formState.errors["ticker"] && (
                  <ErrorField>{form.formState.errors["ticker"].message}</ErrorField>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="preferenceIcon">
                  <FormattedMessage {...loyaltyTagEditMessages.icon} />
                </FormLabel>
                <Controller
                  name="icon"
                  control={form.control}
                  render={({ field }) => (
                    <DynamicIconList
                      {...field}
                      iconPack={IconPack.MATERIAL_DESIGN_ICONS}
                      field={{ inputId: "preferenceIcon" }}
                      onChange={({ value }) => field.onChange(value)}
                      hasErrors={false}
                      disabled={false}
                    />
                  )}
                />
              </FormGroup>

              <FormGroup>
                <TranslationComponent allowedLocales={allowedLocales} />
              </FormGroup>

              <div className="flex gap-4">
                <Button variant="primary" type="submit" onClick={form.handleSubmit(handleSubmit)}>
                  <FormattedMessage {...loyaltyTagEditMessages.save} />
                </Button>
                <Button variant="secondary" onClick={handleGoBack}>
                  <FormattedMessage {...loyaltyTagEditMessages.back} />
                </Button>
              </div>
            </Form>
          </FormProvider>
        </Card>
      </Container>
    </ContentWrapper>
  );
}
